import React, { useCallback, useEffect, useMemo, useState, memo } from 'react';
import isNumber from 'lodash/isNumber';
import isNaN from 'lodash/isNaN';
import cx from 'classnames';
import { FormattedMessage } from 'react-intl';
import { CountUp } from '@eeacms/countup';
import { Statistic } from 'semantic-ui-react';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import {
  serializeNodes as _serializeNodes,
  serializeNodesToText,
} from '@plone/volto-slate/editor/render';
import {
  getFieldURL,
  serializeToNodes,
  serializeNodes,
  enhanceElementWithProps,
} from '@eeacms/volto-statistic-block/helpers';

import '@eeacms/volto-statistic-block/StatisticBlock/styles.less';

const propsAreEqual = (prevProps, nextProps) => {
  return prevProps.data === nextProps.data;
};

const StatisticItem = memo(
  ({
    item,
    mode,
    textAlign,
    valueVariation,
    labelVariation,
    extraVariation,
    animation,
  }) => {
    const href = mode !== 'edit' ? getFieldURL(item.href) : '#';
    const StatisticWrapper = href && href !== '#' ? UniversalLink : Statistic;

    const formatter = useCallback(
      (value) => {
        let prefix = item.prefix ? item.prefix : animation?.prefix || '';
        let suffix = item.suffix ? item.suffix : animation?.suffix || '';
        let formattedValue = value.toFixed(animation.decimals);
        return (
          prefix + parseFloat(formattedValue).toLocaleString('de-DE') + suffix
        );
      },
      [item.prefix, item.suffix, animation],
    );

    const renderNodes = (nodes) => {
      const element = _serializeNodes(nodes);
      const enhancedChildren = enhanceElementWithProps(element, {
        animation: {
          ...animation,
          formatter,
        },
      });

      return enhancedChildren;
    };

    const valueNodes = useMemo(
      () => serializeToNodes(item.value),
      [item.value],
    );
    const valueNo = useMemo(
      () => Number(serializeNodesToText(valueNodes)),
      [valueNodes],
    );
    const labelElement = useMemo(
      () => serializeNodes(item.label),
      [item.label],
    );
    const infoElement = useMemo(() => serializeNodes(item.info), [item.info]);

    return (
      <StatisticWrapper
        key={item['@id']}
        {...(href && href !== '#' ? { className: 'ui statistic', href } : {})}
      >
        <Statistic.Value
          className={cx('slate', `text-${textAlign}`, valueVariation)}
        >
          {isNumber(valueNo) && !isNaN(valueNo) ? (
            animation.enabled ? (
              <CountUp
                end={valueNo}
                isCounting
                duration={parseInt(
                  animation.duration > 0 ? animation.duration : 2,
                )}
                decimalPlaces={animation.decimals > 0 ? animation.decimals : 0}
                formatter={formatter}
              />
            ) : (
              formatter(valueNo)
            )
          ) : (
            renderNodes(valueNodes)
          )}
        </Statistic.Value>
        <Statistic.Label
          className={cx('slate', `text-${textAlign}`, labelVariation)}
        >
          {labelElement}
        </Statistic.Label>
        <div className={cx('slate', `text-${textAlign}`, extraVariation)}>
          {infoElement}
        </div>
      </StatisticWrapper>
    );
  },
);

const View = memo(({ data, mode }) => {
  const [reducedMotion, setReducedMotion] = useState(true);
  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  const {
    horizontal = false,
    inverted = false,
    size = 'small',
    widths = 'one',
    items = [],
    styles = {},
  } = data;
  const animation = {
    ...data.animation,
    enabled: mode !== 'edit' && !reducedMotion,
    duration: 4,
  };
  const {
    textAlign = 'center',
    backgroundInverted = 'primary',
    valueVariation = 'secondary',
    labelVariation = 'tertiary',
    extraVariation = 'tertiary',
  } = styles;

  if (!items.length && mode === 'edit')
    return (
      <p>
        <FormattedMessage
          id="Add statistic items"
          defaultMessage="Add statistic items"
        />
      </p>
    );
  return (
    <div
      className={cx({
        [`color-bg-${backgroundInverted}`]: inverted,
      })}
    >
      <Statistic.Group
        horizontal={horizontal}
        inverted={inverted}
        size={size}
        widths={widths}
        backgroundvariant={backgroundInverted}
        valuevariation={valueVariation}
        labelvariation={labelVariation}
        extravariation={extraVariation}
        className={cx('bonn-statistics', textAlign, {
          'ui container': styles.align === 'full',
        })}
      >
        {items.map((item) => {
          const itemData = {
            item,
            mode,
            textAlign,
            valueVariation,
            labelVariation,
            extraVariation,
            animation,
          };
          return <StatisticItem key={item['@id']} {...itemData} />;
        })}
      </Statistic.Group>
    </div>
  );
}, propsAreEqual);

export default View;
